// Centralised case studies — used on /case-studies and the homepage teaser.
//
// CASE STUDY DATA CONTRACT
// ------------------------------------------------------------------
// Every case study supports BOTH an anonymised mode (default — used until
// the client has signed off in writing) AND a fully named mode (rendered
// once `permissionGranted: true`).
//
// REQUIRED FIELDS (always rendered):
//   slug                  Stable URL-safe identifier.
//   title                 Short engagement title.
//   category              Tag (Labour, Performance, Guest Experience, …).
//   propertyTypeTag       One of: "Hotel" | "Restaurant" | "Multi-site" |
//                                  "Independent" | "Group" | "Aparthotel".
//                         Used for filters + Review schema itemReviewed.
//   property              Anonymised property descriptor — used when
//                         permission is NOT granted.
//   engagementLength      Free text (e.g. "16 weeks").
//   before                Snapshot of the operating state before we started.
//                         One sentence, ideally with a hard number.
//   challenge             What the brief was.
//   action                What we actually did in the property.
//   outcome               Measurable result, rounded to the nearest sensible unit.
//
// PERMISSION-GATED FIELDS (only rendered when permissionGranted === true):
//   permissionGranted     Boolean. Default false. Flip to true ONLY after
//                         signed written permission is on file.
//   clientName            Full property/brand name (string).
//   clientLogoUrl         Optional logo path (e.g. "/case-study-logos/foo.png").
//                         If null, only the name is rendered.
//   testimonial           Object: { quote, author, role } or null.
//                         When present, a schema.org Review JSON-LD block
//                         is emitted for the engagement.
//
// HOW TO TURN AN ANONYMISED CASE INTO A NAMED ONE
// ------------------------------------------------------------------
// 1. Confirm written permission is on file (email or signed letter).
// 2. Edit the entry:
//      permissionGranted: true,
//      clientName: "The Hotel Name",
//      clientLogoUrl: "/case-study-logos/the-hotel-name.png",   // optional
//      testimonial: {
//          quote: "One- or two-line quote, in the speaker's actual voice.",
//          author: "Sarah Example",
//          role: "Managing Director, The Hotel Name",
//      },
// 3. Drop the logo file into /app/frontend/public/case-study-logos/.
// 4. Run `yarn build` — the named layout + Review schema render automatically.
// (No template changes required for any new named case study.)

export const CASE_STUDIES = [
    {
        slug: "improving-labour-productivity",
        title: "Improving Labour Productivity",
        category: "Labour",
        propertyTypeTag: "Independent",
        property: "Independent 4-star hotel · 80 keys · South West England",
        engagementLength: "16 weeks",
        before:
            "Labour cost running 4.2 points above benchmark; rotas built on habit, no shared productivity benchmark across HODs.",
        challenge:
            "Labour cost was running 4.2 points above benchmark. Rotas were built around habit rather than demand. Service standards varied between shifts and the head of department team had no shared productivity benchmark to lean on.",
        action:
            "Rebuilt the rota model around forecast occupancy, food and beverage covers and conversion. Reset role design across rooms division and food and beverage. Introduced a weekly labour cadence with HODs and the general manager. Productivity benchmarks were published and made part of the operating conversation.",
        outcome:
            "Labour cost reduced by 220 basis points within 16 weeks. Guest scores held stable. Agency reliance eliminated for housekeeping. Crucially, the operating cadence held twelve weeks after the engagement closed.",
        permissionGranted: false,
        clientName: null,
        clientLogoUrl: null,
        testimonial: null,
    },
    {
        slug: "operational-performance-review",
        title: "Operational Performance Review",
        category: "Performance",
        propertyTypeTag: "Group",
        property: "Boutique hotel group · 3 properties · Midlands",
        engagementLength: "3 weeks",
        before:
            "GOP flat across three trading periods despite revenue growth; no portfolio cadence to surface the leakage.",
        challenge:
            "Revenue was rising but GOP had been flat across the portfolio for three trading periods. Ownership was unclear where the operating leakage sat and had no portfolio cadence in place to surface it.",
        action:
            "Three week operational review across all three properties. One day in each property. Confidential interviews with HODs, P&L deep-dive and a standards walkthrough. A findings session with ownership followed by a sequenced improvement plan with indicative GOP impact ranges by site.",
        outcome:
            "Identified £340,000 of annual GOP recovery opportunity across the portfolio. Owner used the report to reset operating priorities and inform the following year's budget cycle. Two of the three properties had implemented the priority recommendations within nine months.",
        permissionGranted: false,
        clientName: null,
        clientLogoUrl: null,
        testimonial: null,
    },
    {
        slug: "guest-experience-improvement",
        title: "Guest Experience Improvement",
        category: "Guest Experience",
        propertyTypeTag: "Aparthotel",
        property: "Aparthotel operator · 120 keys · Greater London",
        engagementLength: "12 weeks",
        before:
            "Booking.com score 7.8 vs competitor set 8.4; complaint volume rising month on month; corporate repeat custom in decline.",
        challenge:
            "Booking.com score was sitting at 7.8 against a competitor set averaging 8.4. Complaint volume was rising month on month and repeat custom in the corporate segment was visibly in decline.",
        action:
            "Mapped the full guest journey from online research through to post-stay. Rebuilt the SOPs around the moments that actually moved scores. Ran service training with the front office and housekeeping teams. Introduced a monthly quality audit cadence owned by HODs.",
        outcome:
            "Score lifted from 7.8 to 8.5 within a quarter. Complaint volume reduced by 38%. Direct repeat bookings increased noticeably across the following two quarters. The audit cadence is now part of the operator's group standard.",
        permissionGranted: false,
        clientName: null,
        clientLogoUrl: null,
        testimonial: null,
    },
    {
        slug: "pre-opening-operational-setup",
        title: "Pre-Opening Operational Setup",
        category: "Pre-Opening",
        propertyTypeTag: "Hotel",
        property: "New boutique hotel · 45 keys · South East England",
        engagementLength: "9 months (pre-opening)",
        before:
            "No operating spine in place six months out from opening; standards borrowed from another property; no documented operating plan for ownership.",
        challenge:
            "The developer had no operating spine in place six months out from opening. Brand standards were borrowed from a similar property the team had previously worked with. There was no documented operating plan for ownership to review.",
        action:
            "Built the pre-opening plan: operating workstreams, sequence, budget and decision points from breaking ground through to opening day. Authored property-specific SOPs across rooms, food and beverage, housekeeping and back of house. Designed the team structure, hiring sequence and induction programme.",
        outcome:
            "Opened on time, on budget and with the operating layer ready to scale. First-quarter guest scores opened at 8.7 (against a budget assumption of 8.2). Lenders received documented operational evidence ahead of the first refinancing review.",
        permissionGranted: false,
        clientName: null,
        clientLogoUrl: null,
        testimonial: null,
    },
    {
        slug: "food-and-beverage-margin-recovery",
        title: "Food and Beverage Margin Recovery",
        category: "Performance",
        propertyTypeTag: "Restaurant",
        property: "Country house hotel · 60 keys · Cotswolds",
        engagementLength: "10 weeks",
        before:
            "F&B gross margin had drifted 380 basis points below benchmark over two trading years; symptoms visible to chefs, root cause invisible in the P&L.",
        challenge:
            "Food and beverage gross margin had drifted 380 basis points below benchmark over two trading years. The head chef and food and beverage manager could each describe symptoms but neither could point to the root cause in the P&L.",
        action:
            "Engineered the menu against star, plough, puzzle, dog. Recalculated plate cost against current supplier pricing. Introduced a weekly wastage log at department level. Reset stock counting discipline with a monthly variance review owned by the head chef.",
        outcome:
            "Food and beverage gross margin lifted by 290 basis points within ten weeks, with a further 110 basis points captured over the following quarter. The discipline has held through two subsequent supplier price reviews.",
        permissionGranted: false,
        clientName: null,
        clientLogoUrl: null,
        testimonial: null,
    },
    {
        slug: "multi-site-standards-harmonisation",
        title: "Multi-Site Standards Harmonisation",
        category: "Multi-Site",
        propertyTypeTag: "Multi-site",
        property: "Serviced apartment operator · 5 properties · UK wide",
        engagementLength: "20 weeks",
        before:
            "Standards drifting in two of five sites; head office stretched too thin; KPIs measured differently by each property.",
        challenge:
            "Standards were drifting in two of five sites. Head office was stretched too thin to drive consistency. KPIs were measured differently by each property, so the group operating pack was telling ownership a less reliable story each month.",
        action:
            "Cross-site diagnostic on performance, standards and operating cadence. Aligned SOPs and standards across the portfolio with property-specific local variation built in. Built a single KPI pack with a monthly cadence and quarterly review structure for ownership. Targeted intervention in the property at the two underperforming sites.",
        outcome:
            "Portfolio KPI variance narrowed by 60% within twenty weeks. The two underperforming sites were within 5% of the portfolio average within two quarters. The single KPI pack is now the basis of ownership's monthly review.",
        permissionGranted: false,
        clientName: null,
        clientLogoUrl: null,
        testimonial: null,
    },
];

export const CASE_STUDIES_BY_SLUG = CASE_STUDIES.reduce((a, c) => {
    a[c.slug] = c;
    return a;
}, {});

// Render-time helpers used by CaseStudiesPage / homepage teaser.
export const isNamed = (cs) =>
    Boolean(cs && cs.permissionGranted && cs.clientName);

export const displayLabel = (cs) =>
    isNamed(cs) ? cs.clientName : cs.property;
